import { LiveServerMessageSchema, type LiveMember } from '@garmoth/community-contracts';
import { socketUrl } from './client';
import { api } from './endpoints';

export type LiveStatus = 'connecting' | 'live' | 'reconnecting' | 'closed';

interface LiveHandlers {
  roster: (members: LiveMember[]) => void;
  status: (status: LiveStatus) => void;
  error: (message: string | null) => void;
}

const retryDelays = [1000, 2000, 5000, 10_000];

/** A short-lived, one-use ticket keeps the browser session cookie off the socket URL. */
export function connectLiveRoster(partyId: string, handlers: LiveHandlers) {
  let socket: WebSocket | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let connecting = false;
  let retries = 0;
  let stopped = false;

  function retry() {
    if (stopped || timer) return;
    handlers.status('reconnecting');
    const delay = retryDelays[Math.min(retries, retryDelays.length - 1)] ?? 10_000;
    retries += 1;
    timer = setTimeout(() => { timer = undefined; void open(); }, delay);
  }

  async function open() {
    if (stopped || connecting || socket) return;
    connecting = true;
    handlers.status(retries ? 'reconnecting' : 'connecting');
    try {
      const { ticket } = await api.liveTicket(partyId);
      if (stopped) return;
      const path = `/api/parties/${encodeURIComponent(partyId)}/live?ticket=${encodeURIComponent(ticket)}`;
      const next = new WebSocket(socketUrl(path));
      socket = next;
      next.addEventListener('open', () => {
        if (stopped) { next.close(); return; }
        retries = 0;
        handlers.status('live');
        handlers.error(null);
      });
      next.addEventListener('message', event => {
        if (stopped || typeof event.data !== 'string') return;
        let value: unknown;
        try { value = JSON.parse(event.data); } catch { return; }
        const parsed = LiveServerMessageSchema.safeParse(value);
        if (!parsed.success) return;
        if (parsed.data.type === 'roster') {
          handlers.roster(parsed.data.members);
          handlers.error(null);
        } else handlers.error(parsed.data.message);
      });
      next.addEventListener('close', () => {
        if (socket === next) socket = undefined;
        retry();
      });
      next.addEventListener('error', () => handlers.error('Party connection lost. Retrying.'));
    } catch (cause) {
      if (stopped) return;
      handlers.error(cause instanceof Error ? cause.message : 'Could not connect to the party.');
      retry();
    } finally {
      connecting = false;
    }
  }

  void open();
  return function stop() {
    stopped = true;
    clearTimeout(timer);
    timer = undefined;
    socket?.close();
    socket = undefined;
    handlers.status('closed');
  };
}
